/* eslint-disable max-lines */
/* eslint-disable no-plusplus */
/* eslint-disable max-len */
/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable simple-import-sort/imports */
/* eslint-disable react/jsx-indent */
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { Input, ScrollShadow, Select, SelectItem } from '@heroui/react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { ArrowLeft, ArrowRight, ArrowUpRight, Check, CheckCircle, ChevronDown, Loader, Star, XCircle } from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

import { auth } from '../../../../config/firebase';
import { useHeroToast } from '../../../../hooks';
import { useCreateOrganization, useUpdateOrganization } from '../../../../queries';
import { checkDomainAvailability as checkDomainAvailabilityApi } from '../../../../queries/organizationsQueryFirebase/useOrganizationMutations';
import { Plan } from '../../../../types';
import { inputProps, selectProps } from '../../../../utils';

import { AddressAutocomplete } from './AddressAutocomplete';
import {
  CheckboxIcon,
  CoordinateHint,
  DomainInput,
  DomainPrefix,
  DomainPreview,
  DomainSuffix,
  FieldLabel,
  FormArea,
  FormFields,
  InputRow,
  LanguageCheckbox,
  LanguageCount,
  LanguageGrid,
  LanguageName,
  MapWrapper,
  NavigationButtons,
  NavButton,
  PendingDots,
  PhoneInput,
  PhoneInputWrapper,
  PhonePrefixButton,
  PhonePrefixDropdown,
  PhonePrefixItem,
  PostalCodeHint,
  PostalCodeInput,
  StarButton,
  StarsRow,
  StatusButton,
  StatusDescription,
  StatusIcon,
  StatusOverlay,
  StatusTitle,
  StepConnector,
  StepContent,
  StepDescription,
  StepIndicator,
  StepItem,
  StepLabel,
  StepSubtitle,
  StepTitle,
  VerticalStepper,
  WizardLayout,
} from './CreateOrganizationWizard.styled';

interface MaskFormat {
  mask: string;
  placeholder: string;
}

interface PhoneFormat {
  prefix: string;
  mask: string;
  placeholder: string;
}

const applyMask = (raw: string, mask: string): string => {
  let result = '';
  let rawIndex = 0;

  for (let i = 0; i < mask.length && rawIndex < raw.length; i++) {
    const maskChar = mask[i];
    const inputChar = raw[rawIndex];

    if (maskChar === '9') {
      if (/\d/.test(inputChar)) {
        result += inputChar;
        rawIndex++;
      } else {
        rawIndex++;
        i--;
      }
    } else if (maskChar === 'A') {
      if (/[a-zA-Z]/.test(inputChar)) {
        result += inputChar.toUpperCase();
        rawIndex++;
      } else {
        rawIndex++;
        i--;
      }
    } else if (maskChar === '*') {
      if (/[a-zA-Z0-9]/.test(inputChar)) {
        result += inputChar.toUpperCase();
        rawIndex++;
      } else {
        rawIndex++;
        i--;
      }
    } else {
      result += maskChar;
    }
  }

  return result;
};

const stripMaskLiterals = (formatted: string, mask: string): string => {
  let result = '';
  for (let i = 0; i < formatted.length && i < mask.length; i++) {
    const maskChar = mask[i];
    if (maskChar === '9' || maskChar === 'A' || maskChar === '*') {
      result += formatted[i];
    }
  }
  return result;
};

const COUNTRIES = [
  { code: 'pl', countryCode: 'PL', name: 'Polska', currency: 'PLN', postal: { mask: '99-999', placeholder: '31-021' } as MaskFormat, phone: { prefix: '+48', mask: '999 999 999', placeholder: '123 456 789' } as PhoneFormat },
  { code: 'gb', countryCode: 'GB', name: 'United Kingdom', currency: 'GBP', postal: { mask: '**** ***', placeholder: 'SW1A 1AA' } as MaskFormat, phone: { prefix: '+44', mask: '9999 999999', placeholder: '7911 123456' } as PhoneFormat },
  { code: 'de', countryCode: 'DE', name: 'Deutschland', currency: 'EUR', postal: { mask: '99999', placeholder: '10115' } as MaskFormat, phone: { prefix: '+49', mask: '999 99999999', placeholder: '151 12345678' } as PhoneFormat },
  { code: 'es', countryCode: 'ES', name: 'España', currency: 'EUR', postal: { mask: '99999', placeholder: '28001' } as MaskFormat, phone: { prefix: '+34', mask: '999 999 999', placeholder: '612 345 678' } as PhoneFormat },
  { code: 'fr', countryCode: 'FR', name: 'France', currency: 'EUR', postal: { mask: '99999', placeholder: '75001' } as MaskFormat, phone: { prefix: '+33', mask: '9 99 99 99 99', placeholder: '6 12 34 56 78' } as PhoneFormat },
  { code: 'it', countryCode: 'IT', name: 'Italia', currency: 'EUR', postal: { mask: '99999', placeholder: '00100' } as MaskFormat, phone: { prefix: '+39', mask: '999 999 9999', placeholder: '312 345 6789' } as PhoneFormat },
  { code: 'cz', countryCode: 'CZ', name: 'Česko', currency: 'CZK', postal: { mask: '999 99', placeholder: '110 00' } as MaskFormat, phone: { prefix: '+420', mask: '999 999 999', placeholder: '601 123 456' } as PhoneFormat },
  { code: 'sk', countryCode: 'SK', name: 'Slovensko', currency: 'EUR', postal: { mask: '999 99', placeholder: '811 01' } as MaskFormat, phone: { prefix: '+421', mask: '999 999 999', placeholder: '901 123 456' } as PhoneFormat },
  { code: 'no', countryCode: 'NO', name: 'Norge', currency: 'NOK', postal: { mask: '9999', placeholder: '0150' } as MaskFormat, phone: { prefix: '+47', mask: '999 99 999', placeholder: '412 34 567' } as PhoneFormat },
  { code: 'se', countryCode: 'SE', name: 'Sverige', currency: 'SEK', postal: { mask: '999 99', placeholder: '111 22' } as MaskFormat, phone: { prefix: '+46', mask: '99 999 99 99', placeholder: '70 123 45 67' } as PhoneFormat },
  { code: 'ru', countryCode: 'RU', name: 'Россия', currency: 'RUB', postal: { mask: '999999', placeholder: '101000' } as MaskFormat, phone: { prefix: '+7', mask: '999 999-99-99', placeholder: '912 345-67-89' } as PhoneFormat },
  { code: 'ua', countryCode: 'UA', name: 'Україна', currency: 'UAH', postal: { mask: '99999', placeholder: '01001' } as MaskFormat, phone: { prefix: '+380', mask: '99 999 99 99', placeholder: '50 123 45 67' } as PhoneFormat },
  { code: 'gr', countryCode: 'GR', name: 'Ελλάδα', currency: 'EUR', postal: { mask: '999 99', placeholder: '104 31' } as MaskFormat, phone: { prefix: '+30', mask: '999 999 9999', placeholder: '691 234 5678' } as PhoneFormat },
];

const CURRENCIES = [
  { code: 'PLN', name: 'Polski złoty', symbol: 'zł' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'CZK', name: 'Koruna česká', symbol: 'Kč' },
  { code: 'NOK', name: 'Norske kroner', symbol: 'kr' },
  { code: 'SEK', name: 'Svenska kronor', symbol: 'kr' },
  { code: 'RUB', name: 'Российский рубль', symbol: '₽' },
  { code: 'UAH', name: 'Українська гривня', symbol: '₴' },
];

const MAX_LANGUAGES = 8;

const LANGUAGES = [
  { code: 'pl', countryCode: 'PL', name: 'Polski' },
  { code: 'en', countryCode: 'GB', name: 'English' },
  { code: 'de', countryCode: 'DE', name: 'Deutsch' },
  { code: 'es', countryCode: 'ES', name: 'Español' },
  { code: 'fr', countryCode: 'FR', name: 'Français' },
  { code: 'it', countryCode: 'IT', name: 'Italiano' },
  { code: 'cz', countryCode: 'CZ', name: 'Čeština' },
  { code: 'sk', countryCode: 'SK', name: 'Slovenčina' },
  { code: 'no', countryCode: 'NO', name: 'Norsk' },
  { code: 'se', countryCode: 'SE', name: 'Svenska' },
  { code: 'ru', countryCode: 'RU', name: 'Русский' },
  { code: 'ua', countryCode: 'UA', name: 'Українська' },
  { code: 'gr', countryCode: 'GR', name: 'Ελληνικά' },
];

const GOOGLE_MAPS_LIBRARIES: ('places')[] = ['places'];
const GOOGLE_MAPS_API_KEY = 'AIzaSyBfROJsmdp_wGan1h6lRe3H9C5bSCtQZxo';
const MAP_CONTAINER_STYLE = { width: '100%', height: '350px' };
const DEFAULT_CENTER = { lat: 52.2297, lng: 21.0122 };

const STEP_LABELS = [
  { label: 'Organization Data', desc: 'Basic information' },
  { label: 'Geolocation', desc: 'Address coordinates' },
  { label: 'Contact Data', desc: 'Email and phone' },
  { label: 'Currency', desc: 'Default currency' },
  { label: 'Domain', desc: 'Web address' },
  { label: 'Languages', desc: 'Supported languages' },
  { label: 'Configuration', desc: 'Organization plan' },
];

interface OrganizationWizardInitialValues {
  // Step 0: Organization Data
  stars?: number;
  name?: string;
  country?: string;
  city?: string;
  street?: string;
  buildingNumber?: string;
  apartmentNumber?: string;
  postalCode?: string;
  // Step 1: Geolocation
  latitude?: string;
  longitude?: string;
  // Step 2: Contact Data
  email?: string;
  phone?: string;
  phoneCountry?: string;
  // Step 3: Currency
  currency?: string;
  // Step 4: Domain
  domain?: string;
  // Step 5: Languages
  selectedLanguages?: string[];
  // Step 6: Configuration
  owner?: string;
  plan?: Plan;
  isTestMode?: boolean;
  testExpirationDate?: Date | null;
  // Step 7: AI Settings
  aiTokenLimit?: number;
}

interface CreateOrganizationWizardProps {
  initialValues?: OrganizationWizardInitialValues;
  isEdit?: boolean;
  organizationId?: string;
}

export const CreateOrganizationWizard: FC<CreateOrganizationWizardProps> = ({
  initialValues,
  isEdit = false,
  organizationId,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useHeroToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'pending' | 'success'>('idle');

  // Step 0: Organization Data
  const [stars, setStars] = useState(initialValues?.stars ?? 3);
  const [name, setName] = useState(initialValues?.name ?? '');
  const [country, setCountry] = useState(initialValues?.country ?? 'pl');
  const [city, setCity] = useState(initialValues?.city ?? '');
  const [street, setStreet] = useState(initialValues?.street ?? '');
  const [buildingNumber, setBuildingNumber] = useState(initialValues?.buildingNumber ?? '');
  const [apartmentNumber, setApartmentNumber] = useState(initialValues?.apartmentNumber ?? '');
  const [postalCode, setPostalCode] = useState(initialValues?.postalCode ?? '');
  const [cityCoords, setCityCoords] = useState<{ lat: number; lng: number } | undefined>();

  // Step 1: Geolocation
  const [latitude, setLatitude] = useState(initialValues?.latitude ?? '');
  const [longitude, setLongitude] = useState(initialValues?.longitude ?? '');

  // Step 2: Contact Data
  const [email, setEmail] = useState(initialValues?.email ?? '');
  const [phone, setPhone] = useState(initialValues?.phone ?? '');
  const [phoneCountry, setPhoneCountry] = useState(initialValues?.phoneCountry ?? initialValues?.country ?? 'pl');
  const [showPhonePrefixDropdown, setShowPhonePrefixDropdown] = useState(false);
  const phonePrefixRef = useRef<HTMLDivElement>(null);

  // Step 3: Currency
  const [currency, setCurrency] = useState(initialValues?.currency ?? 'PLN');

  // Step 4: Domain
  const [domain, setDomain] = useState(initialValues?.domain ?? '');
  const [domainStatus, setDomainStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>(isEdit ? 'available' : 'idle');
  const [domainCheckTimeout, setDomainCheckTimeout] = useState<NodeJS.Timeout | null>(null);

  // Step 5: Languages. Domyślnie nic nie zaznaczamy; językiem domyślnym (baseLang)
  // jest pierwszy element listy — admin może go zmienić selectem poniżej.
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>(initialValues?.selectedLanguages ?? []);

  // Step 6: Configuration (Plan). Owner i Test Mode usunięte z kreatora —
  // wartości z initialValues zostają tylko w payloadzie, by edycja ich nie kasowała.
  const [plan, setPlan] = useState<Plan>(initialValues?.plan ?? 'BASIC');
  const owner = initialValues?.owner ?? '';
  const isTestMode = initialValues?.isTestMode ?? false;
  const testExpirationDate = initialValues?.testExpirationDate ?? null;

  // Limit tokenów AI nie jest już ustawiany w kreatorze — backend nadaje domyślny
  // (10M), a zmiana idzie osobnym ekranem „AI Limits". Zostawiamy wartość w payloadzie.
  const aiTokenLimit = initialValues?.aiTokenLimit ?? 10000000;

  const { mutateAsync: createOrganization } = useCreateOrganization();
  const { mutateAsync: updateOrganization } = useUpdateOrganization();

  const handleCountryChange = useCallback((newCountry: string) => {
    if (newCountry === country) return;
    setCountry(newCountry);
    setCity('');
    setStreet('');
    setPostalCode('');
    setPhone('');
    setPhoneCountry(newCountry);
    setCityCoords(undefined);
    setLatitude('');
    setLongitude('');
    const newCountryData = COUNTRIES.find((c) => c.code === newCountry);
    if (newCountryData) {
      setCurrency(newCountryData.currency);
    }
  }, [country]);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const mapCenter = useMemo(() => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    if (Number.isFinite(lat) && Number.isFinite(lng) && (Math.abs(lat) > 0 || Math.abs(lng) > 0)) {
      return { lat, lng };
    }
    return DEFAULT_CENTER;
  }, [latitude, longitude]);

  const onMarkerDragEnd = useCallback((e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      setLatitude(String(e.latLng.lat()));
      setLongitude(String(e.latLng.lng()));
    }
  }, []);

  const selectedCountry = useMemo(() => COUNTRIES.find((c) => c.code === country), [country]);
  const selectedPhoneCountry = useMemo(() => COUNTRIES.find((c) => c.code === phoneCountry), [phoneCountry]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (phonePrefixRef.current && !phonePrefixRef.current.contains(e.target as Node)) {
        setShowPhonePrefixDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePhoneCountryChange = useCallback((code: string) => {
    setPhoneCountry(code);
    setPhone('');
    setShowPhonePrefixDropdown(false);
  }, []);

  const checkDomainAvailability = useCallback(async (domainToCheck: string) => {
    if (!domainToCheck || domainToCheck.length < 3) {
      setDomainStatus('idle');
      return;
    }

    setDomainStatus('checking');

    try {
      const available = await checkDomainAvailabilityApi(
        domainToCheck,
        isEdit ? organizationId : undefined,
      );
      setDomainStatus(available ? 'available' : 'taken');
    } catch (error) {
      setDomainStatus('idle');
    }
  }, [isEdit, organizationId]);

  const handleDomainChange = useCallback((newDomain: string) => {
    const sanitized = newDomain.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setDomain(sanitized);

    // Skip domain check in edit mode if it's the same as initial value
    if (isEdit && sanitized === initialValues?.domain) {
      setDomainStatus('available');
      return;
    }

    if (domainCheckTimeout) {
      clearTimeout(domainCheckTimeout);
    }

    if (sanitized.length < 3) {
      setDomainStatus('idle');
    } else {
      const timeout = setTimeout(() => {
        checkDomainAvailability(sanitized);
      }, 800);
      setDomainCheckTimeout(timeout);
    }
  }, [domainCheckTimeout, checkDomainAvailability, isEdit, initialValues?.domain]);

  const toggleLanguage = useCallback((code: string) => {
    setSelectedLanguages((prev) => {
      if (prev.includes(code)) {
        return prev.filter((c) => c !== code);
      }
      if (prev.length >= MAX_LANGUAGES) return prev;
      return [...prev, code];
    });
  }, []);

  // Ustaw język domyślny (baseLang) = przenieś kod na początek listy.
  const setDefaultLanguage = useCallback((code: string) => {
    setSelectedLanguages((prev) => (
      prev.includes(code) ? [code, ...prev.filter((c) => c !== code)] : prev
    ));
  }, []);

  const isStepValid = useCallback((step: number): boolean => {
    switch (step) {
      case 0:
        return !!(name.trim() && country && city.trim() && street.trim() && buildingNumber.trim() && postalCode.trim());
      case 1:
        return !!(latitude.trim() && longitude.trim());
      case 2:
        return !!(email.trim());
      case 3:
        return !!(currency);
      case 4:
        return !!(domain.trim() && domainStatus === 'available');
      case 5:
        return selectedLanguages.length >= 1;
      case 6:
        // Krok konfiguracji: plan ma zawsze wartość domyślną, nic dodatkowego nie wymagamy.
        return true;
      default:
        return true;
    }
  }, [name, country, city, street, buildingNumber, postalCode, latitude, longitude, email, currency, domain, domainStatus, selectedLanguages]);

  const canProceed = isStepValid(currentStep);

  const handleNext = () => {
    if (currentStep < STEP_LABELS.length - 1 && canProceed) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  const handleSubmit = async () => {
    if (!canProceed || submitStatus === 'pending') return;

    setSubmitStatus('pending');

    const phoneWithPrefix = phone
      ? `${selectedPhoneCountry?.phone.prefix || ''}${phone}`
      : '';

    const payload = {
      name: name.trim(),
      city: city.trim(),
      street: street.trim(),
      buildingNumber: buildingNumber.trim(),
      apartmentNumber: apartmentNumber.trim(),
      postalCode: postalCode.trim(),
      country,
      latitude: parseFloat(latitude) || 0,
      longitude: parseFloat(longitude) || 0,
      email: email.trim(),
      phone: phoneWithPrefix,
      domain,
      languages: selectedLanguages,
      baseLang: selectedLanguages[0] || 'pl',
      stars,
      currency,
      plan,
      isTestMode,
      testExpirationDate: testExpirationDate ? testExpirationDate.toISOString() : null,
      accountManager: owner.trim(),
      aiTokenLimit,
    };

    if (isEdit && organizationId) {
      try {
        const { currentUser } = auth;
        if (!currentUser) {
          setSubmitStatus('idle');
          showError('You must be logged in to update an organization');
          return;
        }

        await updateOrganization({ ...payload, id: organizationId });

        setSubmitStatus('success');
        showSuccess('Organization updated successfully');
        await queryClient.invalidateQueries({ queryKey: ['organizations'] });
        await queryClient.invalidateQueries({ queryKey: ['organization', organizationId] });
        navigate('/organizations');
      } catch (error: any) {
        console.error('Error updating organization:', error);
        setSubmitStatus('idle');
        showError(error?.message || 'Organization update failed');
      }
      return;
    }

    try {
      const { currentUser } = auth;
      if (!currentUser) {
        setSubmitStatus('idle');
        showError('You must be logged in to create an organization');
        return;
      }

      await createOrganization(payload);

      setSubmitStatus('success');
      showSuccess('Organization created successfully');
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
      navigate('/organizations');
    } catch (error: any) {
      console.error('Error creating organization:', error);
      setSubmitStatus('idle');
      showError(error?.message || 'Organization creation failed');
    }
  };

  const isLastStep = currentStep === STEP_LABELS.length - 1;

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <>
            <StepTitle>Organization Data</StepTitle>
            <StepSubtitle>Provide basic information about your organization</StepSubtitle>
            <FormFields>
              <div>
                <FieldLabel>Stars</FieldLabel>
                <StarsRow>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <StarButton
                      key={s}
                      type="button"
                      $active={s <= stars}
                      onClick={() => setStars(s === stars ? 0 : s)}
                    >
                      <Star fill={s <= stars ? '#E07B2D' : 'none'} />
                    </StarButton>
                  ))}
                </StarsRow>
              </div>
              <Input
                {...inputProps}
                label="Organization Name"
                placeholder="Enter organization name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                isRequired
                autoFocus
              />
              <Select
                {...selectProps}
                label="Country"
                selectedKeys={new Set([country])}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string;
                  if (selected) handleCountryChange(selected);
                }}
                isRequired
                renderValue={(items) => {
                  const item = items[0];
                  const c = COUNTRIES.find((ct) => ct.code === item?.key);
                  return c ? (
                    <span className="flex items-center gap-2">
                      <ReactCountryFlag svg countryCode={c.countryCode} style={{ width: 16, height: 12 }} />
                      {c.name}
                    </span>
                  ) : null;
                }}
              >
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} textValue={c.name}>
                    <span className="flex items-center gap-2">
                      <ReactCountryFlag svg countryCode={c.countryCode} style={{ width: 16, height: 12 }} />
                      {c.name}
                    </span>
                  </SelectItem>
                ))}
              </Select>
              <AddressAutocomplete
                label="City"
                placeholder="Enter city name"
                value={city}
                onChange={(val) => {
                  setCity(val);
                  setStreet('');
                }}
                types={['(cities)']}
                countryCode={selectedCountry?.code}
                onPlaceSelect={(result) => {
                  setLatitude(String(result.latitude));
                  setLongitude(String(result.longitude));
                  setCityCoords({ lat: result.latitude, lng: result.longitude });
                }}
              />
              <AddressAutocomplete
                label="Street"
                placeholder="Enter street name"
                value={street}
                onChange={setStreet}
                types={['address']}
                countryCode={selectedCountry?.code}
                locationBias={cityCoords}
                onPlaceSelect={(result) => {
                  setLatitude(String(result.latitude));
                  setLongitude(String(result.longitude));
                }}
              />
              <InputRow>
                <Input
                  {...inputProps}
                  label="Building Number"
                  placeholder="1"
                  value={buildingNumber}
                  onChange={(e) => setBuildingNumber(e.target.value)}
                />
                <Input
                  {...inputProps}
                  label="Apartment Number"
                  placeholder="10"
                  value={apartmentNumber}
                  onChange={(e) => setApartmentNumber(e.target.value)}
                />
              </InputRow>
              <div>
                <FieldLabel>Postal Code</FieldLabel>
                <PostalCodeInput
                  value={applyMask(postalCode, selectedCountry?.postal.mask || '99999')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const mask = selectedCountry?.postal.mask || '99999';
                    const formatted = applyMask(e.target.value, mask);
                    setPostalCode(stripMaskLiterals(formatted, mask));
                  }}
                  placeholder={selectedCountry?.postal.placeholder}
                  maxLength={selectedCountry?.postal.mask.length}
                />
                <PostalCodeHint>Example: {selectedCountry?.postal.placeholder}</PostalCodeHint>
              </div>
            </FormFields>
          </>
        );

      case 1:
        return (
          <>
            <StepTitle>Geolocation</StepTitle>
            <StepSubtitle>Set precise coordinates for your hotel</StepSubtitle>
            <FormFields>
              <InputRow>
                <Input
                  {...inputProps}
                  label="Latitude"
                  placeholder="52.2297"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                />
                <Input
                  {...inputProps}
                  label="Longitude"
                  placeholder="21.0122"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                />
              </InputRow>
              <CoordinateHint>
                Drag the marker on the map to set coordinates automatically
              </CoordinateHint>
              {isLoaded && (
                <MapWrapper>
                  <GoogleMap
                    mapContainerStyle={MAP_CONTAINER_STYLE}
                    center={mapCenter}
                    zoom={15}
                    options={{
                      mapTypeControl: false,
                      fullscreenControl: false,
                      streetViewControl: false,
                    }}
                  >
                    <Marker
                      draggable
                      position={mapCenter}
                      onDragEnd={onMarkerDragEnd}
                    />
                  </GoogleMap>
                </MapWrapper>
              )}
            </FormFields>
          </>
        );

      case 2:
        return (
          <>
            <StepTitle>Contact Data</StepTitle>
            <StepSubtitle>Provide contact information</StepSubtitle>
            <FormFields>
              <Input
                {...inputProps}
                type="email"
                label="Email"
                placeholder="contact@hotel.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                isRequired
              />
              <div>
                <FieldLabel>Phone</FieldLabel>
                <PhoneInputWrapper ref={phonePrefixRef}>
                  <PhonePrefixButton
                    type="button"
                    onClick={() => setShowPhonePrefixDropdown((prev) => !prev)}
                  >
                    <ReactCountryFlag svg countryCode={selectedPhoneCountry?.countryCode || 'PL'} style={{ width: 16, height: 12 }} />
                    {selectedPhoneCountry?.phone.prefix}
                    <ChevronDown />
                  </PhonePrefixButton>
                  <PhoneInput
                    type="tel"
                    value={applyMask(phone, selectedPhoneCountry?.phone.mask || '999 999 999')}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const mask = selectedPhoneCountry?.phone.mask || '999 999 999';
                      const formatted = applyMask(e.target.value, mask);
                      setPhone(stripMaskLiterals(formatted, mask));
                    }}
                    placeholder={selectedPhoneCountry?.phone.placeholder}
                    maxLength={selectedPhoneCountry?.phone.mask.length}
                  />
                  {showPhonePrefixDropdown && (
                    <PhonePrefixDropdown>
                      {COUNTRIES.map((c) => (
                        <PhonePrefixItem
                          key={c.code}
                          $active={c.code === phoneCountry}
                          onClick={() => handlePhoneCountryChange(c.code)}
                        >
                          <ReactCountryFlag svg countryCode={c.countryCode} style={{ width: 16, height: 12 }} />
                          {c.name}
                          <span>{c.phone.prefix}</span>
                        </PhonePrefixItem>
                      ))}
                    </PhonePrefixDropdown>
                  )}
                </PhoneInputWrapper>
              </div>
            </FormFields>
          </>
        );

      case 3:
        return (
          <>
            <StepTitle>Currency</StepTitle>
            <StepSubtitle>Select your organization's default currency</StepSubtitle>
            <FormFields>
              <Select
                {...selectProps}
                label="Currency"
                selectedKeys={new Set([currency])}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as string;
                  if (selected) setCurrency(selected);
                }}
                isRequired
                description="This will be the default currency for all prices"
              >
                {CURRENCIES.map((curr) => (
                  <SelectItem key={curr.code} textValue={`${curr.code} - ${curr.name}`}>
                    <div className="flex items-center justify-between w-full">
                      <span className="font-semibold">{curr.code}</span>
                      <span className="text-sm text-gray-600">{curr.name}</span>
                      <span className="text-lg">{curr.symbol}</span>
                    </div>
                  </SelectItem>
                ))}
              </Select>
            </FormFields>
          </>
        );

      case 4:
        return (
          <>
            <StepTitle>Domain</StepTitle>
            <StepSubtitle>Choose your unique web address</StepSubtitle>
            <FormFields>
              <div>
                <FieldLabel>Domain</FieldLabel>
                <DomainPreview>
                  <DomainPrefix>https://</DomainPrefix>
                  <DomainInput
                    placeholder="your-hotel"
                    value={domain}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleDomainChange(e.target.value)}
                  />
                  <DomainSuffix>.hotelspot.site</DomainSuffix>
                  {domain.length >= 3 && (
                    <div style={{ position: 'absolute', right: '140px', top: '50%', transform: 'translateY(-50%)' }}>
                      {domainStatus === 'checking' && (
                        <Loader className="animate-spin" size={20} color="#E07B2D" />
                      )}
                      {domainStatus === 'available' && (
                        <CheckCircle size={20} color="#10b981" />
                      )}
                      {domainStatus === 'taken' && (
                        <XCircle size={20} color="#ef4444" />
                      )}
                    </div>
                  )}
                </DomainPreview>
                {domain && (
                  <PostalCodeHint>
                    https://{domain}.hotelspot.site
                  </PostalCodeHint>
                )}
                {domain.length >= 3 && domainStatus === 'available' && (
                  <PostalCodeHint style={{ color: '#10b981' }}>
                    Domain is available
                  </PostalCodeHint>
                )}
                {domain.length >= 3 && domainStatus === 'taken' && (
                  <PostalCodeHint style={{ color: '#ef4444' }}>
                    Domain is already taken
                  </PostalCodeHint>
                )}
                {domain.length >= 3 && domainStatus === 'checking' && (
                  <PostalCodeHint style={{ color: '#E07B2D' }}>
                    Checking availability...
                  </PostalCodeHint>
                )}
              </div>
            </FormFields>
          </>
        );

      case 5: {
        const atLimit = selectedLanguages.length >= MAX_LANGUAGES;
        return (
          <>
            <StepTitle>Languages</StepTitle>
            <StepSubtitle>Select supported languages (maximum {MAX_LANGUAGES})</StepSubtitle>
            <FormFields>
              <LanguageCount>
                Selected: {selectedLanguages.length} / {MAX_LANGUAGES}
              </LanguageCount>
              <LanguageGrid>
                {LANGUAGES.map((lang) => {
                  const isSelected = selectedLanguages.includes(lang.code);
                  const isDisabled = !isSelected && atLimit;
                  return (
                    <LanguageCheckbox
                      key={lang.code}
                      type="button"
                      $selected={isSelected}
                      $disabled={isDisabled}
                      disabled={isDisabled}
                      onClick={() => toggleLanguage(lang.code)}
                    >
                      <CheckboxIcon $checked={isSelected}>
                        {isSelected && <Check size={12} />}
                      </CheckboxIcon>
                      <ReactCountryFlag svg countryCode={lang.countryCode} style={{ width: 18, height: 14, flexShrink: 0 }} />
                      <LanguageName>{lang.name}</LanguageName>
                    </LanguageCheckbox>
                  );
                })}
              </LanguageGrid>
              {selectedLanguages.length > 0 && (
                <Select
                  {...selectProps}
                  label="Default language (baseLang)"
                  placeholder="Select default language"
                  description="Zapisywany jako pierwszy w bazie (baseLang)"
                  selectedKeys={new Set([selectedLanguages[0]])}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    if (selected) setDefaultLanguage(selected);
                  }}
                >
                  {selectedLanguages.map((code) => {
                    const lang = LANGUAGES.find((l) => l.code === code);
                    return (
                      <SelectItem key={code} textValue={lang?.name ?? code}>
                        {lang?.name ?? code}
                      </SelectItem>
                    );
                  })}
                </Select>
              )}
            </FormFields>
          </>
        );
      }

      case 6:
        return (
          <>
            <StepTitle>Configuration</StepTitle>
            <StepSubtitle>Set organization plan</StepSubtitle>
            <FormFields>
              <Select
                {...selectProps}
                label="Plan *"
                placeholder="Select organization plan"
                selectedKeys={new Set([plan])}
                onSelectionChange={(keys) => {
                  const selected = Array.from(keys)[0] as Plan;
                  setPlan(selected);
                }}
                description="Organization subscription plan"
                isRequired
              >
                <SelectItem key="BASIC" textValue="BASIC">BASIC</SelectItem>
                <SelectItem key="STANDARD" textValue="STANDARD">STANDARD</SelectItem>
                <SelectItem key="PRO" textValue="PRO">PRO</SelectItem>
                <SelectItem key="ULTIMATE" textValue="ULTIMATE">ULTIMATE</SelectItem>
                <SelectItem key="CUSTOM" textValue="CUSTOM">CUSTOM</SelectItem>
                <SelectItem key="TEST" textValue="TEST">TEST</SelectItem>
              </Select>
            </FormFields>
          </>
        );

      default:
        return null;
    }
  };

  if (submitStatus === 'pending') {
    return (
      <StatusOverlay>
        <StatusIcon $variant="pending">
          <Loader color="#1a4d4d" className="animate-spin" />
        </StatusIcon>
        <StatusTitle>{isEdit ? 'Updating organization...' : 'Creating organization...'}</StatusTitle>
        <StatusDescription>{isEdit ? 'Please wait while we update your organization' : 'Please wait while we set up your organization'}</StatusDescription>
        <PendingDots>
          <span />
          <span />
          <span />
        </PendingDots>
      </StatusOverlay>
    );
  }

  if (submitStatus === 'success') {
    return (
      <StatusOverlay>
        <StatusIcon $variant="success">
          <CheckCircle color="#059669" />
        </StatusIcon>
        <StatusTitle>{isEdit ? 'Organization updated successfully!' : 'Organization created successfully!'}</StatusTitle>
        <StatusDescription>{isEdit ? 'Your organization has been updated' : 'Your organization has been created and is ready to use'}</StatusDescription>
        <StatusButton type="button" onClick={() => navigate('/organizations')}>
          Go to Organizations
          <ArrowUpRight size={18} />
        </StatusButton>
      </StatusOverlay>
    );
  }

  return (
    <WizardLayout>
      <VerticalStepper>
        {STEP_LABELS.map((step, index) => (
          <div key={step.label}>
            <StepItem
              type="button"
              $active={currentStep === index}
              $completed={currentStep > index}
              $disabled={index > currentStep}
              onClick={() => handleStepClick(index)}
            >
              <StepIndicator $active={currentStep === index} $completed={currentStep > index}>
                {currentStep > index ? <Check size={14} /> : index + 1}
              </StepIndicator>
              <StepContent>
                <StepLabel $active={currentStep === index} $completed={currentStep > index}>
                  {step.label}
                </StepLabel>
                <StepDescription $active={currentStep === index}>
                  {step.desc}
                </StepDescription>
              </StepContent>
            </StepItem>
            {index < STEP_LABELS.length - 1 && (
              <StepConnector $completed={currentStep > index} />
            )}
          </div>
        ))}
      </VerticalStepper>

      <FormArea>
        <ScrollShadow hideScrollBar className="max-h-[calc(100vh-200px)] pb-8">
          {renderStepContent()}

        <NavigationButtons>
          <div>
            {currentStep > 0 && (
            <NavButton type="button" $variant="secondary" onClick={handleBack}>
              <ArrowLeft />
              Back
            </NavButton>
            )}
          </div>
          <NavButton
            type="button"
            $variant="primary"
            onClick={isLastStep ? handleSubmit : handleNext}
            disabled={!canProceed}
          >
            {(() => {
              if (!isLastStep) return 'Next';
              if (isEdit) return 'Update Organization';
              return 'Create Organization';
            })()}
            {!isLastStep && <ArrowRight />}
          </NavButton>
        </NavigationButtons>
        </ScrollShadow>
      </FormArea>
    </WizardLayout>
  );
};
